import { ShieldAlert } from "lucide-react";
import { STATUS, fmtDate } from "@/lib/checkins";

const SHORT = { green: "Meeting", amber: "Building", red: "Attention", not_assessable: "No info" };

function Cell({ d }) {
  if (!d) return <td className="px-2 py-1.5 text-center text-xs text-zinc-300">—</td>;
  const s = STATUS[d.status] || STATUS.not_assessable;
  const Icon = s.icon;
  return (
    <td className="px-1.5 py-1.5">
      <span className={`inline-flex w-full items-center justify-center gap-1 rounded border px-1 py-0.5 text-2xs font-semibold ${s.chip}`} title={`${s.label}${d.dose ? ` · ${d.dose}` : ""}`}>
        <Icon className="h-3 w-3" aria-hidden="true" />{SHORT[d.status]}{d.overridden_by ? "*" : ""}
      </span>
    </td>
  );
}

// 12-month domain history; markers for non-typical weeks, version and program changes.
export function TrendGrid({ submissions, programChanges, onOpen }) {
  if (!submissions.length) return null;
  const domains = submissions[submissions.length - 1].domains.map((d) => ({ code: d.code, label: d.label }));
  return (
    <div className="overflow-x-auto thin-scroll" data-testid="checkin-trend-grid">
      <table className="min-w-full border-separate border-spacing-0 text-sm">
        <thead>
          <tr>
            <th className="sticky left-0 bg-white px-2 py-1 text-left text-2xs font-semibold uppercase tracking-[0.15em] text-zinc-500">Domain</th>
            {submissions.map((s, i) => {
              const prev = submissions[i - 1];
              const versionChange = prev && prev.semantic_version !== s.semantic_version;
              const programChange = programChanges.some((p) => p > (prev?.submitted_at?.slice(0, 10) || "") && p <= s.submitted_at.slice(0, 10));
              return (
                <th key={s.id} className="min-w-[96px] px-1.5 py-1 align-bottom">
                  <button type="button" onClick={() => onOpen(s.id)} className="w-full rounded px-1 py-0.5 text-xs font-semibold text-sky-800 hover:bg-sky-50" data-testid={`checkin-trend-open-${s.id}`}>
                    {fmtDate(s.submitted_at)}
                  </button>
                  <div className="flex flex-wrap justify-center gap-0.5 text-2xs font-semibold">
                    {s.typical_week !== "yes" && <span className="rounded bg-zinc-200 px-1 text-zinc-700">Atypical</span>}
                    {versionChange && <span className="rounded bg-violet-100 px-1 text-violet-800">v{s.semantic_version}</span>}
                    {programChange && <span className="rounded bg-sky-100 px-1 text-sky-800">Program</span>}
                    {s.safety_severity && <ShieldAlert className="h-3.5 w-3.5 text-red-700" aria-label="Safety flag" />}
                    {s.release_status !== "released" && <span className="rounded bg-amber-100 px-1 text-amber-900">Held</span>}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {domains.map((dom) => (
            <tr key={dom.code} data-testid={`checkin-trend-row-${dom.code}`}>
              <td className="sticky left-0 whitespace-nowrap bg-white px-2 py-1.5 text-xs font-medium text-zinc-800">{dom.label}</td>
              {submissions.map((s) => <Cell key={s.id} d={s.domains.find((d) => d.code === dom.code)} />)}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mt-1 text-2xs text-zinc-400">* practitioner override · declines are flagged only after two consecutive typical weeks</p>
    </div>
  );
}
